// Dynamic pricing calculations.
// Formula (per design guidelines):
//   running_cost = cost; then for each cost-line apply % of its 'base' OR flat amount
//   final_selling_price = running_cost / (1 - margin%)   (margin on selling price)
//
// All amounts are PER UNIT in the product's input currency, then converted to display currency,
// then multiplied by quantity for totals.

const num = (v) => {
  const n = Number(v);
  return isNaN(n) || !isFinite(n) ? 0 : n;
};

export function computeBreakdown(product, costLines, marginPercent) {
  const cost = num(product.cost);
  let runningCost = cost;
  let cumulativeShipping = 0;
  let cumulativeCustoms = 0;

  const lines = [];

  for (const line of costLines) {
    let baseAmount = 0;
    switch (line.base) {
      case "cost":
        baseAmount = cost;
        break;
      case "cost_plus_shipping":
        baseAmount = cost + cumulativeShipping;
        break;
      case "cost_plus_shipping_customs":
        baseAmount = cost + cumulativeShipping + cumulativeCustoms;
        break;
      case "running":
      default:
        baseAmount = runningCost;
        break;
    }

    let lineAmount = 0;
    if (line.type === "percent") {
      lineAmount = (baseAmount * num(line.value)) / 100;
    } else {
      lineAmount = num(line.value);
    }

    runningCost += lineAmount;

    if (line.id === "shipping") cumulativeShipping += lineAmount;
    if (line.id === "customs") cumulativeCustoms += lineAmount;

    lines.push({
      id: line.id,
      label: line.label,
      type: line.type,
      value: num(line.value),
      base: line.base,
      baseAmount,
      amount: lineAmount,
      runningTotal: runningCost,
    });
  }

  const margin = num(marginPercent);
  const totalCostBeforeMargin = runningCost;

  let sellingPrice;
  let marginAmount;
  if (margin >= 100) {
    // Avoid divide by zero / negative; treat as infinite margin
    sellingPrice = totalCostBeforeMargin;
    marginAmount = 0;
  } else {
    sellingPrice = totalCostBeforeMargin / (1 - margin / 100);
    marginAmount = sellingPrice - totalCostBeforeMargin;
  }

  const qty = Math.max(0, num(product.quantity));

  return {
    cost,
    lines,
    totalCostBeforeMargin,
    marginPercent: margin,
    marginAmount,
    sellingPricePerUnit: sellingPrice,
    quantity: qty,
    totalSellingPrice: sellingPrice * qty,
    totalLandedCost: totalCostBeforeMargin * qty,
  };
}

export function formatMoney(amount, currencySymbol = "£", digits = 2) {
  if (amount === null || amount === undefined || isNaN(amount)) return `${currencySymbol}0.00`;
  const sign = amount < 0 ? "-" : "";
  const abs = Math.abs(amount);
  const formatted = abs.toLocaleString("en-GB", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return `${sign}${currencySymbol}${formatted}`;
}
